/**
 * useHighlights Hook
 *
 * Custom hook for managing article highlights including creation,
 * retrieval, deletion, and detachment detection.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  createHighlight as createHighlightAPI,
  getArticleHighlights,
  deleteHighlight as deleteHighlightAPI,
  deleteAllHighlights as deleteAllHighlightsAPI,
  checkHighlightDetachment,
  reattachHighlights as reattachHighlightsAPI,
} from '../services/highlights';

/**
 * Calculate SHA-256 hash for summary version tracking
 * @param {string} text - Text to hash
 * @returns {Promise<string>} Hex string of hash
 */
async function calculateSummaryHash(text) {
  const encoder = new TextEncoder();
  const data = encoder.encode(text || '');
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Normalize text for comparison by collapsing whitespace
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
function normalizeText(text) {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Extract text position in summary content
 * @param {string} summaryText - Full summary text
 * @param {string} selectedText - Selected text to find
 * @returns {Object|null} Position object with start and end, or null if not found
 */
function findTextPosition(summaryText, selectedText) {
  if (!summaryText || !selectedText) {
    return null;
  }

  // Try exact match first
  let start = summaryText.indexOf(selectedText);
  if (start !== -1) {
    return {
      start,
      end: start + selectedText.length,
    };
  }

  // Try normalized match (collapse whitespace)
  const normalizedSummary = normalizeText(summaryText);
  const normalizedSelected = normalizeText(selectedText);

  const normalizedStart = normalizedSummary.indexOf(normalizedSelected);
  if (normalizedStart === -1) {
    return null;
  }

  // Map normalized position back to original text
  let charCount = 0;
  let originalPos = 0;

  for (let i = 0; i < summaryText.length; i++) {
    if (summaryText[i].match(/\S/)) {
      if (charCount === normalizedStart) {
        originalPos = i;
        break;
      }
      charCount++;
    }
  }

  return {
    start: originalPos,
    end: originalPos + selectedText.length,
  };
}

/**
 * Extract context around selected text
 * @param {string} fullText - Full text content
 * @param {number} start - Start position
 * @param {number} end - End position
 * @param {number} contextLength - Length of context to extract (default: 50)
 * @returns {Object} Object with prefix and suffix context
 */
function extractContext(fullText, start, end, contextLength = 50) {
  const prefix = fullText.substring(Math.max(0, start - contextLength), start);
  const suffix = fullText.substring(end, Math.min(fullText.length, end + contextLength));

  return { prefix, suffix };
}

export function useHighlights(articleId, summaryText) {
  const [highlights, setHighlights] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [detachmentInfo, setDetachmentInfo] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Fetch highlights for the current article
   */
  const fetchHighlights = useCallback(async () => {
    if (!articleId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await getArticleHighlights(articleId, false);
      setHighlights(response.highlights || []);
    } catch (err) {
      console.error('Error fetching highlights:', err);
      setError(err.message);
      setHighlights([]);
    } finally {
      setIsLoading(false);
    }
  }, [articleId]);

  /**
   * Check for detached highlights
   */
  const checkDetachment = useCallback(async () => {
    if (!articleId) return;

    try {
      const response = await checkHighlightDetachment(articleId);
      setDetachmentInfo(response);
    } catch (err) {
      console.error('Error checking detachment:', err);
    }
  }, [articleId]);

  /**
   * Create a new highlight
   * @param {string} selectedText - The exact text being highlighted
   * @param {Object} selectionRange - Selection range with start/end positions
   * @returns {Promise<Object>} Created highlight
   */
  const createHighlight = useCallback(
    async (selectedText, selectionRange = null, renderedText = null) => {
      if (!articleId || !summaryText || !selectedText) {
        throw new Error('Missing required data for highlight creation');
      }

      // Use rendered text if provided, otherwise fall back to summary text
      const textForPositioning = renderedText || summaryText;

      try {
        setIsCreating(true);
        setError(null);

        // Calculate summary hash for version tracking
        const summaryHash = await calculateSummaryHash(summaryText);

        // Find text position if not provided
        let position = selectionRange;
        if (!position) {
          position = findTextPosition(textForPositioning, selectedText);
          if (!position) {
            throw new Error('Could not find selected text in rendered content');
          }
        }

        // Extract context around selection
        const context = extractContext(textForPositioning, position.start, position.end);

        // Build W3C Web Annotation structure with multi-selector strategy
        const annotation = {
          '@context': 'http://www.w3.org/ns/anno.jsonld',
          type: 'Annotation',
          body: {
            type: 'TextualBody',
            value: selectedText,
            format: 'text/plain',
          },
          target: {
            selector: [
              // RangeSelector (fast, fragile)
              {
                type: 'RangeSelector',
                startSelector: {
                  type: 'XPathSelector',
                  value: '/html/body/div/div/div',
                },
                endSelector: {
                  type: 'XPathSelector',
                  value: '/html/body/div/div/div',
                },
              },
              // TextPositionSelector (fast, moderately fragile)
              {
                type: 'TextPositionSelector',
                start: position.start,
                end: position.end,
              },
              // TextQuoteSelector (slow, robust)
              {
                type: 'TextQuoteSelector',
                exact: selectedText,
                prefix: context.prefix,
                suffix: context.suffix,
              },
            ],
          },
        };

        // Create highlight via API
        const highlightData = {
          annotation,
          exact_text: selectedText,
          summary_version: summaryHash,
        };

        const newHighlight = await createHighlightAPI(articleId, highlightData);

        // Add to local state
        setHighlights((prev) => [...prev, newHighlight]);

        return newHighlight;
      } catch (err) {
        console.error('Error creating highlight:', err);
        setError(err.message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [articleId, summaryText]
  );

  /**
   * Delete a highlight
   * @param {number} highlightId - Highlight ID to delete
   */
  const deleteHighlight = useCallback(async (highlightId) => {
    try {
      setError(null);
      await deleteHighlightAPI(highlightId);

      // Remove from local state
      setHighlights((prev) => prev.filter((h) => h.id !== highlightId));
    } catch (err) {
      console.error('Error deleting highlight:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete all highlights for the current article
   */
  const deleteAllHighlights = useCallback(async () => {
    if (!articleId) return;

    try {
      setError(null);
      await deleteAllHighlightsAPI(articleId);

      // Clear local state
      setHighlights([]);
    } catch (err) {
      console.error('Error deleting all highlights:', err);
      setError(err.message);
      throw err;
    }
  }, [articleId]);

  /**
   * Re-attach detached highlights
   * @param {number} similarityThreshold - Minimum similarity threshold (0.0-1.0)
   * @returns {Promise<Object>} Re-attachment results
   */
  const reattachHighlights = useCallback(
    async (similarityThreshold = 0.8) => {
      if (!articleId) return;

      try {
        setError(null);
        const response = await reattachHighlightsAPI(articleId, similarityThreshold);

        // Refresh highlights after re-attachment
        await fetchHighlights();
        await checkDetachment();

        return response;
      } catch (err) {
        console.error('Error re-attaching highlights:', err);
        setError(err.message);
        throw err;
      }
    },
    [articleId, fetchHighlights, checkDetachment]
  );

  // Fetch highlights when article changes
  useEffect(() => {
    if (articleId) {
      fetchHighlights();
      checkDetachment();
    }
  }, [articleId, fetchHighlights, checkDetachment]);

  return {
    highlights,
    isLoading,
    isCreating,
    error,
    detachmentInfo,
    createHighlight,
    deleteHighlight,
    deleteAllHighlights,
    fetchHighlights,
    reattachHighlights,
    checkDetachment,
  };
}
